import { useI18n } from '@/hooks/useI18n';
import type { EstadoConCobertura } from '@/features/inversionistas/datos/tipos';
import {
  ALTO_DEL_MOSAICO,
  ANCHO_DEL_MOSAICO,
  CASILLAS,
  LADO_DE_CASILLA,
  intensidad,
} from './mosaico';
import styles from './MosaicoMexico.module.css';

interface Props {
  porEstado: readonly EstadoConCobertura[];
}

/**
 * El mapa de cobertura: una casilla por estado, encendida si hay registros.
 *
 * Se dibuja en SVG y sin librería de mapas. No es tacañería: una librería
 * geográfica traería el contorno real de México, y con contornos reales los
 * estados chicos con señal desaparecen mientras los grandes sin nada dominan
 * la vista. Aquí cada estado ocupa lo mismo y el ojo lee cobertura, que es la
 * pregunta.
 *
 * El componente **no decide qué se puede enseñar**: pinta lo que recibe. Poner
 * esa decisión aquí sería ponerla en el navegador, que es donde no se sostiene.
 *
 * > **Lo que cambió respecto a la versión anterior, y es una decisión:**
 * > `EstadoConCobertura` ya **no tiene** `organizaciones`. Antes el `<title>` de
 * > cada casilla las escribía cuando el servidor las mandaba —o sea, sólo en el
 * > panel de administración, que ya no existe—. Se podía haber dejado el campo
 * > como opcional; no se dejó, y por eso: un campo opcional se llena. Si el tipo
 * > no lo tiene, el compilador impide que un nombre de organización llegue
 * > jamás a este SVG.
 *
 * El color **nunca es la única señal**: cada casilla con registros escribe su
 * número dentro, la leyenda nombra cada nivel, y el `<title>` —que es el tooltip
 * nativo del SVG y lo que lee un lector de pantalla— dice el dato en palabras.
 */
export function MosaicoMexico({ porEstado }: Props) {
  const { t } = useI18n();
  const m = t.area.mapa;

  const porClave = new Map(porEstado.map((estado) => [estado.clave, estado]));

  return (
    <div>
      <div className={styles.marco}>
        <svg
          className={styles.svg}
          viewBox={`0 0 ${ANCHO_DEL_MOSAICO} ${ALTO_DEL_MOSAICO}`}
          role="img"
          aria-label={m.descripcion}
        >
          {CASILLAS.map((casilla) => {
            const datos = porClave.get(casilla.clave);
            const registros = datos?.registros ?? 0;
            const nivel = intensidad(registros);

            const titulo = datos
              ? m.estadoConRegistros(datos.nombre, registros)
              : m.estadoSinRegistros(casilla.clave);

            return (
              <g key={casilla.clave} className={nivel > 0 ? styles.encendida : undefined}>
                <rect
                  className={styles[`n${nivel}` as 'n0' | 'n1' | 'n2' | 'n3']}
                  x={casilla.x}
                  y={casilla.y}
                  width={LADO_DE_CASILLA}
                  height={LADO_DE_CASILLA}
                  rx={9}
                />
                <title>{titulo}</title>
                <text className={styles.clave} x={casilla.x + 10} y={casilla.y + 22}>
                  {casilla.clave}
                </text>
                {registros > 0 && (
                  <text className={styles.cuenta} x={casilla.x + 10} y={casilla.y + 52}>
                    {registros}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      <div className={styles.leyenda}>
        <span className={styles.muestra}>
          <span className={[styles.cuadro, styles.n0].join(' ')} />
          {m.leyenda.sinSenal}
        </span>
        <span className={styles.muestra}>
          <span className={[styles.cuadro, styles.n1].join(' ')} />
          {m.leyenda.uno}
        </span>
        <span className={styles.muestra}>
          <span className={[styles.cuadro, styles.n2].join(' ')} />
          {m.leyenda.dos}
        </span>
        <span className={styles.muestra}>
          <span className={[styles.cuadro, styles.n3].join(' ')} />
          {m.leyenda.tresOmas}
        </span>
      </div>
    </div>
  );
}
